import React from "react";
import axios from "axios";
import get from "lodash/get";
import Box from "@mui/material/Box";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLoader } from "app/components/page-loader";
import { useLocalStorage, useSessionStorage } from "react-use";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStoreActions, useStoreState } from "app/state/store/hooks";

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const searchParams = useSearchParams()[0];
  const { user, error, isLoading, isAuthenticated, getAccessTokenSilently } =
    useAuth0();

  const setFavouritedActivityId = useLocalStorage(
    "favourited-activity-id",
    ""
  )[1];
  const clearUserAuthRequest = useLocalStorage("user-auth-request", {
    type: null,
    loading: false,
  })[2];
  const [storedToken, setStoredToken] = React.useState("");

  const storedUser = useStoreState((state) =>
    get(state.user, "data.data", null)
  );
  const fetchUserAction = useStoreActions(
    (actions) => actions.user.authGetFetch
  );
  const fetchIntercomUserAction = useStoreActions(
    (actions) => actions.IntercomUser.authGetFetch
  );
  const addFavouriteAction = useStoreActions(
    (actions) => actions.CreateBookmark.authPostFetch
  );

  const [authRedirectData, _, clearAuthRedirectData] = useLocalStorage(
    "auth-redirect-data",
    {
      step: -1,
      pathname: "/",
    }
  );
  const setActiveStep = useSessionStorage("publisher-active-step", -1)[1];

  const [storedInvitation, _setStoredInvitation, clearStoredInvitation] =
    useLocalStorage("invitation-data", null);

  const addFavourite = async (token: string) => {
    const values = {
      itemId: searchParams.get("favouriteActivityId") ?? "",
      name: searchParams.get("favouriteActivityName") ?? "",
      path: searchParams.get("favouriteActivityPath") ?? "",
    };
    await addFavouriteAction({
      dontEncryptAuthCall: true,
      values: { ...values, userId: storedUser?.user_id, token },
    });
    setFavouritedActivityId(searchParams.get("favouriteActivityId") ?? "");
  };

  const afterLoginActions = async (token: string) => {
    if (searchParams.get("favouriteActivityId")) {
      await addFavourite(token);
    }
    if (searchParams.get("shareLink")) {
      localStorage.setItem(
        "share-dialog-data",
        JSON.stringify({
          emails: searchParams.get("shareEmails") ?? "",
          link: searchParams.get("shareLink") ?? "",
          pathname: searchParams.get("sharePathname") ?? "",
          id: searchParams.get("shareId") ?? "",
        })
      );
    }
  };

  const onSuccessfulUserLoad = async () => {
    if (storedToken) {
      await fetchIntercomUserAction({
        dontEncryptAuthCall: true,
        values: { token: storedToken, email: storedUser.email },
      });
      await afterLoginActions(storedToken);
    } else {
      const token = await getAccessTokenSilently();
      fetchIntercomUserAction({ values: { token } });
      await afterLoginActions(token);
    }
    navigate(authRedirectData?.pathname ?? "/");
    setActiveStep(authRedirectData?.step ?? -1);
    clearUserAuthRequest();
  };

  const completeInvitationProcess = async () => {
    const token = get(storedInvitation, "token", null);
    if (!token || !user) {
      return;
    }
    if (storedInvitation) {
      return await axios
        .post(`${process.env.REACT_APP_API}/invitation/accept`, {
          token,
          userId: user.sub,
          email: user.email,
        })
        .then(() => {
          clearStoredInvitation();
          return;
        })
        .catch((error) => {
          console.error("Error accepting invitation:", error);
          return;
        });
    } else {
      return;
    }
  };

  React.useEffect(() => {
    return () => {
      clearAuthRedirectData();
    };
  }, []);

  React.useEffect(() => {
    getAccessTokenSilently().then((token: string) => {
      setStoredToken(token);
      fetchUserAction({ values: { token } });
    });
  }, []);

  React.useEffect(() => {
    if (isAuthenticated && !isLoading) {
      if (storedToken) {
        completeInvitationProcess().then(() => {
          fetchUserAction({ values: { token: storedToken } });
          getAccessTokenSilently().then((token: string) => {
            fetchUserAction({ values: { token } });
          });
        });
      }
    }
  }, [isAuthenticated, isLoading]);

  React.useEffect(() => {
    if (storedUser) {
      onSuccessfulUserLoad();
    }
  }, [storedUser]);

  if (error) {
    return <div>{error.message}</div>;
  }

  return (
    <Box
      sx={{
        top: 0,
        left: 0,
        zIndex: 1500,
        width: "100vw",
        height: "100vh",
        display: "flex",
        position: "absolute",
        alignItems: "center",
        background: "#f7f7f7",
        justifyContent: "center",
      }}
    >
      <PageLoader />
    </Box>
  );
};
